import {
  useBrandSupplierAddMutation,
  useBrandSupplierListQuery,
  useBrandSupplierSelectedListQuery,
  useBrandSupplierStarMutation,
} from 'api';
import { useCallback, useEffect, useState } from 'react';
import { ErrorMappingValueType, OrderType, PaginationType, SortParamsType } from 'typings';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, FormInstance, notification, TablePaginationConfig, TableProps } from 'components';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { BrandSupplierDataType } from 'feature/brandTabs/pricingAndSuppliers/supplierGrid/index';
import { dateFormatter } from 'constants/index';
import { isObjectEmpty } from 'utils/helpers';
import { ADMIN_ROUTES } from 'constants/routes';

export interface IBrandSupplierFilterParams {
  supplierIds?: string;
  minMarkup?: number;
  maxMarkup?: number;
  minAverageMargin?: number;
  maxAverageMargin?: number;
  minDealsCount?: number;
  maxDealsCount?: number;
  minDealsAmount?: number;
  maxDealsAmount?: number;
  minAverageLeadTime?: number;
  maxAverageLeadTime?: number;
  pricelistUpdatedDateFrom?: string;
  pricelistUpdatedDateTo?: string;
  dateRange?: any;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};
export const useSupplierGrid = () => {
  const [filterDrawerState, setFilterDrawerState] = useState(false);
  const [mainSupplierSetModalState, setMainSupplierSetModalState] = useState<boolean>(false);
  const [supplierRemoveModalState, setSupplierRemoveModalState] = useState<boolean>(false);
  const [addSupplierModalState, setAddSupplierModalState] = useState<boolean>(false);
  const [mainSupplierRemoveModalState, setMainSupplierRemoveModalState] = useState<boolean>(false);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [filters, setFilters] = useState<IBrandSupplierFilterParams>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [suppliersToAdd, setSuppliersToAdd] = useState<string[]>([]);
  const [isAddVisible, setIsAddVisible] = useState<boolean>(false);
  const { id } = useParams() as { id: string };
  const [filtersForm] = Form.useForm<FormInstance>();
  const navigate = useNavigate();
  const { data: selectedSuppliersData = [] } = useBrandSupplierSelectedListQuery({ brandId: id });
  const { data = {}, isLoading } = useBrandSupplierListQuery({
    brandId: id,
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });
  const [addBrandSupplier] = useBrandSupplierAddMutation();
  const [supplierStar] = useBrandSupplierStarMutation();

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({ message: message });
    },
    [notificationApi]
  );

  const onOpenFilters = useCallback(() => setFilterDrawerState(true), []);
  const onCloseFilters = useCallback(() => setFilterDrawerState(false), []);

  const onRedirectToSupplier = useCallback(
    (id: string) => navigate(`${ADMIN_ROUTES.suppliers.view}/${id}/general`),
    [navigate]
  );

  const onMainSetModalStateChange = useCallback((id?: string) => {
    if (id) {
      setSupplierId(id);
    } else {
      setSupplierId(null);
    }
    setMainSupplierSetModalState((prevState) => !prevState);
  }, []);
  const onSupplierRemoveModalStateChange = useCallback((id?: string) => {
    if (id) {
      setSupplierId(id);
    } else {
      setSupplierId(null);
    }
    setSupplierRemoveModalState((prevState) => !prevState);
  }, []);
  const onMainRemoveModalStateChange = useCallback(() => {
    setMainSupplierRemoveModalState((prevState) => !prevState);
  }, []);
  const onAddSupplierModalStateChange = useCallback(() => {
    setAddSupplierModalState((prevState) => !prevState);
  }, []);

  const handleSupplierChange = useCallback(
    (value: string[]) => {
      const areArraysEqual = value.sort().join() === [...selectedSuppliersData].sort().join();
      setIsAddVisible(!areArraysEqual);
      setSuppliersToAdd(value);
    },
    [selectedSuppliersData]
  );

  const applyFilters = useCallback(
    (values: IBrandSupplierFilterParams) => {
      const _filters: any = {
        ...(values.supplierIds && { supplierIds: values.supplierIds }),
        ...(values.minMarkup && { minMarkup: values.minMarkup }),
        ...(values.maxMarkup && { maxMarkup: values.maxMarkup }),
        ...(values.minAverageMargin && { minAverageMargin: values.minAverageMargin }),
        ...(values.maxAverageMargin && { maxAverageMargin: values.maxAverageMargin }),
        ...(values.minDealsCount && { minDealsCount: values.minDealsCount }),
        ...(values.maxDealsCount && { maxDealsCount: values.maxDealsCount }),
        ...(values.minDealsAmount && { minDealsAmount: values.minDealsAmount }),
        ...(values.maxDealsAmount && { maxDealsAmount: values.maxDealsAmount }),
        ...(values.minAverageLeadTime && { minAverageLeadTime: values.minAverageLeadTime }),
        ...(values.maxAverageLeadTime && { maxAverageLeadTime: values.maxAverageLeadTime }),
        ...(values.dateRange?.[0] && {
          pricelistUpdatedDateFrom: dateFormatter(values.dateRange[0], 'start'),
        }),
        ...(values.dateRange?.[1] && {
          pricelistUpdatedDateTo: dateFormatter(values.dateRange[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  const handleTableChange: TableProps<BrandSupplierDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<BrandSupplierDataType> | SorterResult<BrandSupplierDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const onStarChange = useCallback(
    async (starred: boolean, supplierId: string) => {
      try {
        const payload = {
          supplierId,
          brandId: id,
        };
        const { data, error }: Record<string, any> = await supplierStar(payload);
        if (data?.success) {
          openNotificationWithIcon({
            type: 'success',
            message: `Supplier star was successfully ${starred ? 'removed' : 'added'}.`,
          });
          setIsAddVisible(false);
        } else if (error?.data?.success === false) {
          Object.values(error?.data?.data || {}).forEach((message) => {
            openNotificationWithIcon({
              type: 'error',
              message: message as string,
            });
          });
        } else if (error?.data?.error) {
          openNotificationWithIcon({
            type: 'error',
            message: 'Something went wrong!',
          });
        }
      } catch (err) {
        console.error('Error staring user supplier:', err);
        openNotificationWithIcon({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.',
        });
      }
    },
    [id, supplierStar, openNotificationWithIcon]
  );

  const onSupplierSelectCancel = useCallback(() => {
    setSuppliersToAdd(selectedSuppliersData);
    setIsAddVisible(false);
  }, [selectedSuppliersData]);
  const onAddSupplier = useCallback(async () => {
    try {
      const payload = {
        supplierIds: suppliersToAdd,
        brandId: id,
      };
      const { data, error }: Record<string, any> = await addBrandSupplier(payload);
      if (data?.success) {
        openNotificationWithIcon({
          type: 'success',
          message: 'The Suppliers list is successfully updated.',
        });
        setIsAddVisible(false);
      } else if (error?.data?.success === false) {
        Object.values(error?.data?.data || {}).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message: message as string,
          });
        });
      } else if (error?.data?.error) {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    } catch (err) {
      console.error('Error adding user supplier:', err);
      openNotificationWithIcon({
        type: 'error',
        message: 'An unexpected error occurred. Please try again.',
      });
    }
  }, [suppliersToAdd, id, addBrandSupplier, openNotificationWithIcon]);

  useEffect(() => {
    if (Array.isArray(selectedSuppliersData) && selectedSuppliersData.length) {
      setSuppliersToAdd(selectedSuppliersData);
    }
  }, [selectedSuppliersData]);

  return {
    data: data.content || [],
    isLoading,
    handleTableChange,
    onMainSetModalStateChange,
    onMainRemoveModalStateChange,
    onSupplierRemoveModalStateChange,
    onAddSupplierModalStateChange,
    onOpenFilters,
    onCloseFilters,
    onSearch,
    pagination,
    total: data.totalElements ?? 0,
    handleSupplierChange,
    onSupplierSelectCancel,
    suppliersToAdd,
    contextHolder,
    onAddSupplier,
    onStarChange,
    applyFilters,
    resetFilters,
    isAddVisible,
    mainSupplierSetModalState,
    mainSupplierRemoveModalState,
    supplierRemoveModalState,
    addSupplierModalState,
    supplierId,
    filtersForm,
    filterDrawerState,
    onRedirectToSupplier,
    isQueryApplied: !isObjectEmpty(filters) || search,
  };
};
